import {
  IonHeader,
  IonPage,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonIcon,
  IonContent,
  IonLabel,
  IonInput,
  IonToggle,
  useIonModal
} from "@ionic/react";
import {
  calendarOutline,
  chevronBack,
  helpCircle,
  timeOutline
} from "ionicons/icons";
import { useEffect, useRef, useState } from "react";
import ProgressBar from "../components/ProgressBar";
import { DateRange } from "react-date-range";
import { DAY_IN_MILLISECONDS, WEEK_IN_MILLISECONDS } from "./SetupBot";
import TimePicker from "../components/TimePicker";
import ConfirmationPage from "./Confirmation";
import moment from "moment";
import { useAuthStore } from "../stores/auth.store";
import { addLobby } from "../lib/firebase";
import JoinLobby from "./JoinLobby";

interface HostLobbyProps {
  onDismiss: () => void;
}

function formatShortDate(date: any) {
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec"
  ];

  const dayName = days[date.getDay()];
  const day = date.getDate();
  const monthName = months[date.getMonth()];

  return `${dayName} ${day} ${monthName}`;
}

function to24HourFormat(timeStr: string) {
  const [time, period] = timeStr.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  return `${hours.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")}`;
}

const HostLobby: React.FC<HostLobbyProps> = ({ onDismiss }) => {
  const uid = useAuthStore(state => state.user?.uid);
  const [activePage, setActivePage] = useState(0);
  const [lobbyName, setLobbyName] = useState("");
  const [lobbyBuyIn, setLobbyBuyIn] = useState(3);
  const [lockBots, setLockBots] = useState(false);
  const lobbyNameInputRef = useRef(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [selectedLobby, setSelectedLobby] = useState<any>(null);

  const pageCount = 3;

  const startDateRef: any = new Date();
  const startDate = new Date(startDateRef.getTime() + 3 * DAY_IN_MILLISECONDS);
  const endDate = new Date(startDate.getTime() + WEEK_IN_MILLISECONDS);

  const [dateRange, setDateRange] = useState([
    {
      startDate,
      endDate,
      key: "selection"
    }
  ]);

  const [showJoinLobby, dismissJoinLobby] = useIonModal(JoinLobby, {
    onDismiss: () => dismissJoinLobby(),
    lobby: selectedLobby
  });

  const [presentPopup, dismissPopup] = useIonModal(ConfirmationPage, {
    onDismiss: async (confirmation: any) => {
      dismissPopup();
      if (confirmation) {
        const startDate = moment(dateRange[0].startDate);
        const endDate = moment(dateRange[0].endDate);

        const durationInDays = endDate.diff(startDate, "days");
        const startDateStr = startDate.format("YYYY-MM-DD");
        const endDateStr = endDate.format("YYYY-MM-DD");
        const timeStr = to24HourFormat(startTime || "10:00");
        const lobby: any = {
          name: lobbyName,
          startDate: `${startDateStr} ${timeStr}`,
          endDate: `${endDateStr} ${timeStr}`,
          started: false,
          ended: false,
          duration: durationInDays,
          fantaC: true,
          buyIn: lobbyBuyIn,
          canSpeculate: false,
          speculationBuyIn: 0,
          lockedBots: lockBots,
          private: false,
          startBtc: 1,
          administrators: [uid],
          participants: [uid],
          created: new Date().getTime()
        };
        const response = await addLobby(lobby);
        lobby.id = response.key;
        setSelectedLobby(lobby);
        onDismiss();
        showJoinLobby();
      }
    },
    title: "Create Lobby",
    confirmText: "Create",
    contentHtml: `
      <div class="flex flex-col ion-text-center px-16 py-4 text-primary-30">
        <div class="pb-4 mx-auto">
          You are about to create a lobby named ${lobbyName} with a buy in of ${lobbyBuyIn} QUO.
          The lobby is scheduled to run from ${formatShortDate(
            dateRange[0].startDate
          )} ${startTime} to ${formatShortDate(
      dateRange[0].endDate
    )} ${startTime}.
        </div>
      </div>
    `
  });

  const handleTimeChange = (time: string) => {
    setStartTime(time);
  };

  useEffect(() => {
    if (activePage === 0 && lobbyNameInputRef.current) {
      // @ts-ignore
      lobbyNameInputRef.current.setFocus();
    }
  }, [activePage]);

  const shouldDisableNext = () => {
    switch (activePage) {
      case 0:
        return !lobbyName || lobbyBuyIn < 3;
      case 1:
        return (
          !dateRange[0]?.startDate ||
          !dateRange[0]?.endDate ||
          dateRange[0]?.startDate > dateRange[0]?.endDate
        );
      case 2:
        return !startTime;
      default:
        return false;
    }
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          {activePage > 0 && (
            <IonButtons slot="start">
              <IonButton
                onClick={() => {
                  setActivePage(activePage - 1);
                }}
              >
                <IonIcon className="text-white" icon={chevronBack} />
              </IonButton>
            </IonButtons>
          )}
          <IonTitle>Host Lobby</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={() => {
                onDismiss();
              }}
            >
              <span className="text-white">Cancel</span>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {activePage === 0 && (
          <div className="flex flex-col px-2 h-full">
            <div className="flex items-center justify-between pt-4">
              <div className="text-2xl font-semibold px-2 py-2">
                Create a Lobby
              </div>
              <IonButton
                fill="clear"
                className="pt-2"
                onClick={() => console.log("help create lobby")}
              >
                <IonIcon
                  icon={helpCircle}
                  className="text-primary-30"
                  slot="icon-only"
                />
              </IonButton>
            </div>
            <div className="flex flex-col px-4 pt-6">
              <IonLabel className="py-2">Lobby Name</IonLabel>
              <div className="flex flex-row border border-2 border-primary-50 rounded w-full items-center px-4">
                <IonInput
                  ref={lobbyNameInputRef}
                  placeholder="Lobby Name"
                  onIonChange={e => setLobbyName(e.detail.value || "")}
                  value={lobbyName}
                />
              </div>
            </div>
            <div className="flex flex-col px-4 pt-6">
              <IonLabel className="py-2">Buy in Amount</IonLabel>
              <div className="flex flex-row border border-2 border-primary-50 rounded w-full items-center px-4">
                <IonInput
                  placeholder="Enter amount"
                  min={3}
                  type="number"
                  onIonChange={e => setLobbyBuyIn(Number(e.detail.value))}
                  value={lobbyBuyIn}
                />
                <IonLabel>QUO</IonLabel>
              </div>
            </div>
            <p className="text-sm p-4 text-primary-30">
              When you host a lobby, a QUO commission is earned for all
              participating bots.
            </p>
            <div className="p-4 m-4 border border-primary-50 rounded-lg bg-primary-60 bg-opacity-40">
              <div className="flex justify-between items-center font-semibold">
                Lock Bots?
                <IonToggle
                  checked={lockBots}
                  onIonChange={e => setLockBots(e.detail.checked)}
                />
              </div>
              <p className="text-sm py-2 text-primary-30">
                Once the lobby starts, players can't change their strategy.
                Stick to your initial plan!
              </p>
            </div>
          </div>
        )}
        {activePage === 1 && (
          <div className="flex flex-col px-2 h-full">
            <div className="flex items-center justify-between pt-4">
              <div className="text-2xl font-semibold px-2 py-2">
                Select a Date
              </div>
              <IonButton
                fill="clear"
                className="pt-2"
                onClick={() => console.log("help create lobby")}
              >
                <IonIcon
                  icon={helpCircle}
                  className="text-primary-30"
                  slot="icon-only"
                />
              </IonButton>
            </div>
            <div className="bg-primary-50 m-4 p-4 flex items-center rounded-lg">
              <span className="w-full text-center">
                Select Lobby date range
              </span>
              <IonIcon className="text-2xl" icon={calendarOutline} />
            </div>
            <div className="bg-primary-50 rounded-lg mx-4">
              <DateRange
                className="m-1"
                editableDateInputs={true}
                minDate={startDate}
                onChange={(item: any) => setDateRange([item.selection])}
                moveRangeOnFirstSelection={false}
                ranges={dateRange}
              />
            </div>
          </div>
        )}
        {activePage === 2 && (
          <div className="flex flex-col px-2 h-full">
            <div className="flex items-center justify-between pt-4">
              <div className="text-2xl font-semibold px-2 py-2">
                Select a Start Time
              </div>
              <IonButton
                fill="clear"
                className="pt-2"
                onClick={() => console.log("help start time")}
              >
                <IonIcon
                  icon={helpCircle}
                  className="text-primary-30"
                  slot="icon-only"
                />
              </IonButton>
            </div>
            <div className="bg-primary-50 m-4 p-4 flex items-center rounded-lg">
              <span className="w-full text-center">Select a start time</span>
              <IonIcon className="text-2xl" icon={timeOutline} />
            </div>

            <TimePicker onTimeChange={handleTimeChange} />
          </div>
        )}
        {activePage < pageCount && (
          <div className="fixed bottom-4 w-screen p-2">
            <ProgressBar activePage={activePage} pageCount={pageCount} />
            <IonButton
              color="primary"
              disabled={shouldDisableNext()}
              className="rounded-lg w-full mt-2"
              onClick={() => {
                activePage < pageCount - 1
                  ? setActivePage(activePage + 1)
                  : presentPopup();
              }}
            >
              {activePage < pageCount - 1 ? "Next" : "Create"}
            </IonButton>
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default HostLobby;
